import React, { useRef, useEffect } from "react";
import {
  Box,
  ListItem,
  ListItemAvatar,
  Avatar,
  LinearProgress,
  Typography,
  IconButton,
  Tooltip,
  TextField,
  useTheme,
  Fade,
  Skeleton,
} from "@mui/material";

import CancelOutlined from "@mui/icons-material/CancelOutlined";
import PsychologyOutlined from "@mui/icons-material/PsychologyOutlined";
import SendOutlined from "@mui/icons-material/SendOutlined";
import ThumbDownOutlined from "@mui/icons-material/ThumbDownOutlined";
import ThumbUpOutlined from "@mui/icons-material/ThumbUpOutlined";
import CheckCircleOutlined from "@mui/icons-material/CheckCircleOutlined";

import ProfileAvatar from "components/user/ProfileAvatar";
import NiceMarkdown from "components/global/NiceMarkdown";
import { useDispatch } from "react-redux";
import { updateCopilotFeedback } from "slices/copilot";
import { useState } from "react";

const CopilotResponse = ({
  user,
  message,
  isWaiting,
  streamStatus,
  id,
  lastPrompt,
  copilotPrompt,
  timestamp,
}) => {
  const containerRef = useRef(null);
  const dispatch = useDispatch();
  const theme = useTheme();
  const [badSelected, setBadSelected] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [visible, setVisible] = useState(false);
  const isCopilot = user === "Copilot";

  useEffect(() => {
    setVisible(true);
  }, []);

  const handleFeedback = (result) => {
    const prompt = {
      id: copilotPrompt.id,
      userFeedback: result,
      userFeedbackMessage: feedback,

    };
    dispatch(updateCopilotFeedback({ prompt }));
  };

  const formatTimestamp = (value) => {
    if (!value) return "";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const displayTimestamp = formatTimestamp(timestamp || copilotPrompt?.createdAt || copilotPrompt?.modifiedAt);

  return (
    <Fade in={visible} timeout={300}>
      <ListItem
        sx={{
          padding: 0,
          wordBreak: "break-word",
          display: "flex",
          justifyContent: isCopilot ? "flex-start" : "flex-end",
          mb: 1.5,
        }}
        alignItems="flex-start"
        ref={containerRef}
        key={id}
      >
      <Box
        display="flex"
        flexDirection={isCopilot ? "row" : "row-reverse"}
        gap={1}
        maxWidth="100%"
        alignItems="flex-start"
      >
        <ListItemAvatar sx={{ minWidth: 36, display: "flex", justifyContent: isCopilot ? "flex-start" : "flex-end" }}>
          <Avatar
            sx={{
              width: 28,
              height: 28,
              backgroundColor: isCopilot
                ? theme.palette.primary.main
                : theme.palette.secondary.main,
              fontSize: "0.875rem",
            }}
          >
            {isCopilot ? (
              <PsychologyOutlined sx={{ fontSize: "16px" }} />
            ) : (
              <ProfileAvatar user={user.user} size="28" />
            )}
          </Avatar>
        </ListItemAvatar>

        <Box
          display="flex"
          flexDirection="column"
          maxWidth="calc(100% - 44px)"
          gap={0.5}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent={isCopilot ? "flex-start" : "flex-end"}
            gap={0.75}
            sx={{ mb: 0.5 }}
          >
            <Typography
              sx={{
                fontSize: "0.875rem",
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}
            >
              {isCopilot ? "OpsBlox.io" : user.user.firstName + " " + user.user.lastName}
            </Typography>
            {displayTimestamp && (
              <Typography
                sx={{
                  fontSize: "0.68rem",
                  color: theme.palette.text.disabled,
                  fontWeight: 500,
                }}
              >
                {displayTimestamp}
              </Typography>
            )}
          </Box>

          {isWaiting ? (
            <Box
              sx={{
                backgroundColor: theme.palette.mode === "dark"
                  ? theme.palette.background.alt
                  : theme.palette.background.light,
                padding: theme.spacing(2),
                borderRadius: "12px",
                minWidth: "220px",
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: theme.palette.mode === "dark"
                  ? "none"
                  : "0 2px 8px rgba(0, 0, 0, 0.04)",
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 500,
                  }}
                >
                  {streamStatus || "Thinking..."}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: "3px",
                    "& span": {
                      width: 4,
                      height: 4,
                      borderRadius: "50%",
                      backgroundColor: theme.palette.primary.main,
                      animation: "bounce 1.4s ease-in-out infinite both",
                    },
                    "& span:nth-of-type(1)": { animationDelay: "-0.32s" },
                    "& span:nth-of-type(2)": { animationDelay: "-0.16s" },
                    "@keyframes bounce": {
                      "0%, 80%, 100%": { transform: "scale(0)", opacity: 0.4 },
                      "40%": { transform: "scale(1)", opacity: 1 },
                    },
                  }}
                >
                  <span />
                  <span />
                  <span />
                </Box>
              </Box>
              <Skeleton variant="text" width="90%" height={18} />
              <Skeleton variant="text" width="70%" height={18} />
              <LinearProgress sx={{ height: 3, borderRadius: "2px", mt: 1 }} />
            </Box>
          ) : (
            <React.Fragment>
              <Box
                sx={{
                  backgroundColor: isCopilot
                    ? theme.palette.mode === "dark"
                      ? theme.palette.background.alt
                      : theme.palette.background.light
                    : theme.palette.primary.main,
                  color: isCopilot ? theme.palette.text.primary : "white",
                  padding: theme.spacing(1.5, 2),
                  borderRadius: isCopilot ? "12px 12px 12px 4px" : "12px 12px 4px 12px",
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  border: isCopilot ? `1px solid ${theme.palette.divider}` : "none",
                  boxShadow: theme.palette.mode === "dark"
                    ? "none"
                    : "0 2px 8px rgba(0, 0, 0, 0.04)",
                  "& *": {
                    color: isCopilot ? "inherit" : "white",
                  },
                  "& p": {
                    margin: 0,
                    lineHeight: 1.6,
                  },
                }}
              >
                {isCopilot && !isWaiting && (!message || message.trim() === "") && (copilotPrompt?.jobStatus === "Error" || copilotPrompt?.jobStatus === "Failed") ? (
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.error.main, fontStyle: "italic" }}
                  >
                    Sorry, something went wrong and I could not generate a response. Please try again.
                  </Typography>
                ) : (
                  <NiceMarkdown>{message}</NiceMarkdown>
                )}
              </Box>

              {lastPrompt && copilotPrompt.userFeedback && (
                <Box
                  display="flex"
                  alignItems="center"
                  gap={0.5}
                  sx={{
                    backgroundColor: theme.palette.mode === "dark"
                      ? theme.palette.background.alt
                      : theme.palette.background.light,
                    borderRadius: "8px",
                    padding: theme.spacing(0.75, 1),
                    maxWidth: "fit-content",
                    mt: 0.5,
                  }}
                >
                  <CheckCircleOutlined sx={{ fontSize: "14px", color: theme.palette.success.main }} />
                  <Typography sx={{ fontSize: "0.75rem", color: theme.palette.text.secondary }}>
                    Thanks for your feedback
                  </Typography>
                </Box>
              )}

              {lastPrompt && !copilotPrompt.userFeedback && (
                <Box display="flex" flexDirection="column" gap={0.5} mt={0.5}>
                  <Box display="flex" flexDirection="row" gap={0.5}>
                    <Tooltip title="Good response">
                      <IconButton
                        onClick={() => dispatch(updateCopilotFeedback({
                          prompt: {
                            id: copilotPrompt.id,
                            userFeedback: "Good",
                            userFeedbackMessage: "",
                          }
                        }))}
                        disabled={badSelected}
                        size="small"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        <ThumbUpOutlined sx={{ fontSize: "18px" }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Bad response">
                      <IconButton
                        onClick={() => setBadSelected(true)}
                        disabled={badSelected}
                        size="small"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        <ThumbDownOutlined sx={{ fontSize: "18px" }} />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {badSelected && (
                    <Box display="flex" flexDirection="row" gap={0.5} alignItems="flex-start">
                      <TextField
                        id="feedback"
                        label="Tell us more..."
                        value={feedback}
                        size="small"
                        onChange={(event) => {
                          setFeedback(event.target.value);
                        }}
                        sx={{ flex: 1, maxWidth: "200px" }}
                      />

                      <IconButton
                        onClick={() => {
                          dispatch(updateCopilotFeedback({
                            prompt: {
                              id: copilotPrompt.id,
                              userFeedback: "Bad",
                              userFeedbackMessage: feedback,
                            }
                          }));
                        }}
                        size="small"
                        sx={{ mt: 0.5, color: theme.palette.primary.main }}
                      >
                        <SendOutlined sx={{ fontSize: "18px" }} />
                      </IconButton>

                      <IconButton
                        onClick={() => setBadSelected(false)}
                        size="small"
                        sx={{ mt: 0.5, color: theme.palette.text.secondary }}
                      >
                        <CancelOutlined sx={{ fontSize: "18px" }} />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              )}
            </React.Fragment>
          )}
        </Box>
      </Box>
      </ListItem>
    </Fade>
  );
};

export default CopilotResponse;
